/**
 * Admin Optimizer — Painel de otimização D1: Retenção, Agregação, Cache, Sharding
 */
import { T } from '../styles/tokens';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';

// Estilos reutilizáveis
const card: any = {
  background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12,
  padding: '20px 24px', marginBottom: 16,
};
const label: any = { color: T.textSecondary, fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' };
const input: any = {
  background: T.bgInput || 'rgba(255,255,255,0.06)', border: `1px solid ${T.border}`,
  borderRadius: 8, padding: '8px 12px', color: T.text, fontSize: 14, width: '100%',
};
const btn: any = {
  background: T.primary, color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const btnDanger: any = { ...btn, background: T.danger || '#ef4444' };
const tabBtn = (active: boolean): any => ({
  background: active ? T.primary : 'transparent', color: active ? '#fff' : T.textSecondary,
  border: `1px solid ${active ? T.primary : T.border}`, borderRadius: 8,
  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginRight: 8,
});

// Tabelas gerenciadas
const TABLES = [
  { key: 'runner_v3_events', label: 'Runner V3 Events', desc: '~12K/dia' },
  { key: 'vault_audit_log', label: 'Vault Audit Log', desc: '~13K/dia' },
  { key: 'chat_messages', label: 'Chat Messages', desc: '~2K/dia' },
  { key: 'router_logs', label: 'Router Logs', desc: 'variável' },
  { key: 'llm_usage_logs', label: 'LLM Usage Logs', desc: 'variável' },
  { key: 'wallet_transactions', label: 'Wallet Transactions', desc: 'variável' },
];

const CACHE_ENDPOINTS = [
  { key: 'subscription_status', label: '/subscription/status', defaultTtl: 60 },
  { key: 'customers_me', label: '/customers/me', defaultTtl: 120 },
  { key: 'wallet_balance', label: '/wallet/balance', defaultTtl: 30 },
  { key: 'agents', label: '/agents', defaultTtl: 60 },
  { key: 'plans', label: '/plans', defaultTtl: 3600 },
  { key: 'llm_models', label: '/llm/models', defaultTtl: 300 },
];

/** Tab Sharding — D1 isolado por cliente */
function ShardingTab() {
  const [dbs, setDbs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newCustomerId, setNewCustomerId] = useState('');
  const [creating, setCreating] = useState(false);
  const [migrating, setMigrating] = useState<string | null>(null);
  const [migratingAll, setMigratingAll] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [dbsRes, statusRes] = await Promise.all([
        api.get('/admin/optimizer/customer-dbs'),
        api.get('/admin/optimizer/migration-status'),
      ]);
      setDbs(dbsRes.data?.records ?? []);
      setTotal(dbsRes.data?.total ?? 0);
      setMigrationStatus(statusRes.data ?? null);
    } catch { /* ignorar */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createDB = async () => {
    if (!newCustomerId.trim()) return;
    setCreating(true);
    try {
      await api.post('/admin/optimizer/customer-dbs', { customer_id: newCustomerId.trim() });
      setNewCustomerId('');
      await load();
    } catch (e: any) {
      alert('Erro: ' + (e?.response?.data?.error?.message || e.message));
    }
    setCreating(false);
  };

  const deleteDB = async (id: string) => {
    if (!confirm('Desativar este D1?')) return;
    try {
      await api.delete(`/admin/optimizer/customer-dbs/${id}`);
      await load();
    } catch { /* ignorar */ }
  };

  const migrateCustomer = async (customerId: string) => {
    if (!confirm(`Migrar dados do cliente ${customerId}? (backup R2 será criado)`)) return;
    setMigrating(customerId);
    try {
      await api.post(`/admin/optimizer/migrate-customer/${customerId}`);
      await load();
    } catch (e: any) {
      alert('Erro: ' + (e?.response?.data?.error?.message || e.message));
    }
    setMigrating(null);
  };

  const migrateAll = async () => {
    if (!confirm('Migrar TODOS os clientes? Isso pode demorar. Backup R2 será criado para cada um.')) return;
    setMigratingAll(true);
    try {
      const res = await api.post('/admin/optimizer/migrate-all');
      alert(`Migração concluída! Migrados: ${res.data?.migrated ?? 0}, Falhas: ${res.data?.failed ?? 0}`);
      await load();
    } catch (e: any) {
      alert('Erro: ' + (e?.response?.data?.error?.message || e.message));
    }
    setMigratingAll(false);
  };

  const migStatusColor = (s: string) => {
    if (s === 'completed') return '#10b981';
    if (s === 'migrating') return '#f59e0b';
    if (s === 'partial') return '#f97316';
    return T.textSecondary;
  };

  return (
    <div>
      {/* Status geral da migração */}
      {migrationStatus && (
        <div style={{ ...card, marginBottom: 16 }}>
          <h4 style={{ color: T.text, margin: '0 0 12px' }}>📊 Status da Migração</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
            {[
              { label: 'Clientes', value: migrationStatus.total_customers, color: T.text },
              { label: 'DBs Criados', value: migrationStatus.total_dbs, color: '#3b82f6' },
              { label: 'Completos', value: migrationStatus.completed, color: '#10b981' },
              { label: 'Pendentes', value: migrationStatus.pending, color: T.textSecondary },
              { label: 'Migrando', value: migrationStatus.migrating, color: '#f59e0b' },
              { label: 'Parcial', value: migrationStatus.partial, color: '#f97316' },
              { label: 'Rows Migradas', value: migrationStatus.total_rows, color: '#8b5cf6' },
            ].map(item => (
              <div key={item.label} style={{ background: T.bgSecondary, borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                <div style={{ color: item.color, fontSize: 20, fontWeight: 700 }}>{item.value}</div>
                <div style={{ color: T.textSecondary, fontSize: 11 }}>{item.label}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button style={{ ...btn, background: '#8b5cf6' }} onClick={migrateAll} disabled={migratingAll}>
              {migratingAll ? '⏳ Migrando todos...' : '🚀 Migrar Todos'}
            </button>
          </div>
        </div>
      )}

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: T.text, fontSize: 18, margin: 0 }}>🔀 Customer D1 Isolation</h3>
          <span style={{ color: T.textSecondary, fontSize: 13 }}>Total: {total} DBs</span>
        </div>

        {/* Criar novo */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            style={{ ...input, flex: 1 }}
            placeholder="Customer ID"
            value={newCustomerId}
            onInput={(e: any) => setNewCustomerId(e.target.value)}
          />
          <button style={btn} onClick={createDB} disabled={creating}>
            {creating ? '⏳ Criando...' : '➕ Criar DB'}
          </button>
        </div>

        {/* Lista */}
        {loading ? (
          <p style={{ color: T.textSecondary, textAlign: 'center' }}>Carregando...</p>
        ) : dbs.length === 0 ? (
          <p style={{ color: T.textSecondary, textAlign: 'center' }}>Nenhum D1 de cliente criado ainda.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  <th style={{ color: T.textSecondary, textAlign: 'left', padding: '8px 4px' }}>Customer ID</th>
                  <th style={{ color: T.textSecondary, textAlign: 'left', padding: '8px 4px' }}>D1 Name</th>
                  <th style={{ color: T.textSecondary, textAlign: 'left', padding: '8px 4px' }}>Status</th>
                  <th style={{ color: T.textSecondary, textAlign: 'left', padding: '8px 4px' }}>Migração</th>
                  <th style={{ color: T.textSecondary, textAlign: 'right', padding: '8px 4px' }}>Rows</th>
                  <th style={{ color: T.textSecondary, textAlign: 'left', padding: '8px 4px' }}>Criado em</th>
                  <th style={{ color: T.textSecondary, textAlign: 'right', padding: '8px 4px' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {dbs.map((db: any) => (
                  <tr key={db.id} style={{ borderBottom: `1px solid ${T.border}22` }}>
                    <td style={{ color: T.text, padding: '8px 4px', fontFamily: 'monospace', fontSize: 11 }}>{db.customer_id}</td>
                    <td style={{ color: T.text, padding: '8px 4px' }}>{db.d1_database_name}</td>
                    <td style={{ padding: '8px 4px' }}>
                      <span style={{
                        background: db.status === 'active' ? '#10b98120' : '#ef444420',
                        color: db.status === 'active' ? '#10b981' : '#ef4444',
                        padding: '2px 8px', borderRadius: 4, fontSize: 11,
                      }}>
                        {db.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 4px' }}>
                      <span style={{
                        color: migStatusColor(db.migration_status || 'pending'),
                        fontSize: 11, fontWeight: 600,
                      }}>
                        {db.migration_status || 'pending'}
                      </span>
                    </td>
                    <td style={{ color: T.textSecondary, padding: '8px 4px', textAlign: 'right', fontSize: 11 }}>
                      {db.migrated_rows ?? 0}
                    </td>
                    <td style={{ color: T.textSecondary, padding: '8px 4px', fontSize: 11 }}>
                      {db.created_at ? new Date(db.created_at).toLocaleString('pt-BR') : '—'}
                    </td>
                    <td style={{ padding: '8px 4px', textAlign: 'right', display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      {db.status === 'active' && db.migration_status !== 'completed' && (
                        <button
                          onClick={() => migrateCustomer(db.customer_id)}
                          disabled={migrating === db.customer_id}
                          style={{ ...btn, background: '#3b82f6', padding: '4px 12px', fontSize: 12 }}
                        >
                          {migrating === db.customer_id ? '⏳' : '📦 Migrar'}
                        </button>
                      )}
                      {db.status === 'active' && (
                        <button
                          onClick={() => deleteDB(db.id)}
                          style={{ ...btn, background: '#ef4444', padding: '4px 12px', fontSize: 12 }}
                        >
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminOptimizer() {
  const [tab, setTab] = useState<'retention' | 'aggregation' | 'cache' | 'sharding'>('retention');
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [stats, setStats] = useState<any>(null);
  const [archiveJobs, setArchiveJobs] = useState<any[]>([]);
  const [cacheStats, setCacheStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [settingsR, statsR, jobsR, cacheR] = await Promise.all([
        api.get('/admin/optimizer/settings'),
        api.get('/admin/optimizer/stats'),
        api.get('/admin/optimizer/archive/jobs?limit=10'),
        api.get('/admin/optimizer/cache/stats'),
      ]);
      setSettings((settingsR as any).settings || {});
      setStats((statsR as any) || null);
      setArchiveJobs((jobsR as any).jobs || []);
      setCacheStats((cacheR as any).stats || []);
    } catch (e: any) {
      setMsg('Erro ao carregar: ' + e.message);
    }
    setLoading(false);
  }

  async function save() {
    setSaving(true);
    try {
      await api.put('/admin/optimizer/settings', { settings });
      setMsg('✅ Salvo com sucesso!');
      setTimeout(() => setMsg(''), 3000);
    } catch (e: any) {
      setMsg('❌ Erro: ' + e.message);
    }
    setSaving(false);
  }

  async function runNow() {
    if (!confirm('Executar cleanup/archive agora?')) return;
    try {
      const r = await api.post('/admin/optimizer/archive/run-now');
      setMsg(`✅ Cleanup executado: ${(r as any).processed} rows processados`);
      load();
    } catch (e: any) {
      setMsg('❌ Erro: ' + e.message);
    }
  }

  async function flushCache() {
    if (!confirm('Limpar todo o cache KV?')) return;
    try {
      const r = await api.post('/admin/optimizer/cache/flush');
      setMsg(`✅ Cache limpo: ${(r as any).flushed} chaves removidas`);
      load();
    } catch (e: any) {
      setMsg('❌ Erro: ' + e.message);
    }
  }

  function updateSetting(key: string, value: string) {
    setSettings(prev => ({ ...prev, [key]: value }));
  }

  if (loading) return <div style={{ padding: 40, color: T.textSecondary }}>Carregando...</div>;

  const dbGB = stats?.dbSizeGB || 0;
  const pct = Math.min((dbGB / 10) * 100, 100);
  const hitRate = stats?.cacheHitRate || 0;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ color: T.text, fontSize: 24, fontWeight: 700, marginBottom: 4 }}>⚡ D1 Optimizer</h1>
      <p style={{ color: T.textSecondary, fontSize: 14, marginBottom: 24 }}>
        Gerenciamento de retenção, archiving, agregação e cache do banco D1
      </p>

      {msg && <div style={{ ...card, background: msg.includes('✅') ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', borderColor: msg.includes('✅') ? '#22c55e' : '#ef4444', color: msg.includes('✅') ? '#22c55e' : '#ef4444', padding: '12px 16px', fontSize: 14 }}>{msg}</div>}

      {/* Dashboard topo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div style={card}>
          <div style={{ color: T.textSecondary, fontSize: 12, marginBottom: 8 }}>D1 Usage</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{dbGB.toFixed(2)} GB <span style={{ fontSize: 14, color: T.textSecondary }}>/ 10 GB</span></div>
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 4, height: 8, marginTop: 8, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: pct > 80 ? '#ef4444' : pct > 60 ? '#f59e0b' : T.primary, borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
        </div>
        <div style={card}>
          <div style={{ color: T.textSecondary, fontSize: 12, marginBottom: 8 }}>Total Rows</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{(stats?.totalRows || 0).toLocaleString()}</div>
        </div>
        <div style={card}>
          <div style={{ color: T.textSecondary, fontSize: 12, marginBottom: 8 }}>Cache Hit Rate</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: hitRate > 70 ? '#22c55e' : hitRate > 40 ? '#f59e0b' : T.text }}>{hitRate}%</div>
        </div>
        <div style={card}>
          <div style={{ color: T.textSecondary, fontSize: 12, marginBottom: 8 }}>Projeção</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: (stats?.daysUntilFull || 999) < 60 ? '#ef4444' : T.text }}>
            {stats?.daysUntilFull > 999 ? '∞' : `${stats?.daysUntilFull}d`}
          </div>
          <div style={{ color: T.textSecondary, fontSize: 11 }}>até estouro</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: 20 }}>
        {(['retention', 'aggregation', 'cache', 'sharding'] as const).map(t => (
          <button key={t} style={tabBtn(tab === t)} onClick={() => setTab(t)}>
            {t === 'retention' ? '🗄️ Retenção' : t === 'aggregation' ? '📊 Agregação' : t === 'cache' ? '⚡ Cache' : '🔀 Sharding'}
          </button>
        ))}
      </div>

      {/* Tab Retenção */}
      {tab === 'retention' && (
        <div>
          <div style={{ ...card, padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: T.text, fontSize: 16, margin: 0 }}>Configuração de Retenção por Tabela</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btn} onClick={save} disabled={saving}>{saving ? 'Salvando...' : '💾 Salvar'}</button>
                <button style={btnDanger} onClick={runNow}>▶️ Executar Agora</button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <label style={{ color: T.textSecondary, fontSize: 13 }}>
                <input type="checkbox" checked={settings['optimizer_enabled'] !== 'false'} onChange={e => updateSetting('optimizer_enabled', (e.target as any).checked ? 'true' : 'false')} style={{ marginRight: 6 }} />
                Optimizer habilitado
              </label>
              <label style={{ color: T.textSecondary, fontSize: 13 }}>
                <input type="checkbox" checked={settings['archive_enabled'] !== 'false'} onChange={e => updateSetting('archive_enabled', (e.target as any).checked ? 'true' : 'false')} style={{ marginRight: 6 }} />
                Archiving R2
              </label>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  <th style={{ textAlign: 'left', padding: 8, color: T.textSecondary, fontSize: 12 }}>Tabela</th>
                  <th style={{ textAlign: 'center', padding: 8, color: T.textSecondary, fontSize: 12 }}>Dias</th>
                  <th style={{ textAlign: 'center', padding: 8, color: T.textSecondary, fontSize: 12 }}>Ação</th>
                  <th style={{ textAlign: 'center', padding: 8, color: T.textSecondary, fontSize: 12 }}>Rows</th>
                  <th style={{ textAlign: 'center', padding: 8, color: T.textSecondary, fontSize: 12 }}>Ativo</th>
                </tr>
              </thead>
              <tbody>
                {TABLES.map(t => {
                  const prefix = `optimizer_retention_${t.key}`;
                  return (
                    <tr key={t.key} style={{ borderBottom: `1px solid ${T.border}22` }}>
                      <td style={{ padding: 8 }}>
                        <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>{t.label}</div>
                        <div style={{ color: T.textSecondary, fontSize: 11 }}>{t.desc}</div>
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <input type="number" style={{ ...input, width: 70, textAlign: 'center' }}
                          value={settings[`${prefix}_days`] || '30'}
                          onChange={(e: any) => updateSetting(`${prefix}_days`, e.target.value)} />
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <select style={{ ...input, width: 130 }}
                          value={settings[`${prefix}_action`] || 'DELETE'}
                          onChange={(e: any) => updateSetting(`${prefix}_action`, e.target.value)}>
                          <option value="DELETE">🗑️ Deletar</option>
                          <option value="ARCHIVE_R2">📦 Archive R2</option>
                        </select>
                      </td>
                      <td style={{ padding: 8, textAlign: 'center', color: T.text, fontSize: 14 }}>
                        {(stats?.tableCounts?.[t.key] || 0).toLocaleString()}
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <input type="checkbox" checked={settings[`${prefix}_enabled`] !== 'false'}
                          onChange={(e: any) => updateSetting(`${prefix}_enabled`, e.target.checked ? 'true' : 'false')} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Config global */}
          <div style={card}>
            <h3 style={{ color: T.text, fontSize: 14, marginBottom: 12 }}>Configuração Global</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={label}>Batch size (rows por execução)</label>
                <input type="number" style={input} value={settings['optimizer_cleanup_batch_size'] || '1000'}
                  onChange={(e: any) => updateSetting('optimizer_cleanup_batch_size', e.target.value)} />
              </div>
              <div>
                <label style={label}>Timeout por tabela (segundos)</label>
                <input type="number" style={input} value={settings['optimizer_cleanup_timeout_per_table_s'] || '25'}
                  onChange={(e: any) => updateSetting('optimizer_cleanup_timeout_per_table_s', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Histórico de jobs */}
          {archiveJobs.length > 0 && (
            <div style={card}>
              <h3 style={{ color: T.text, fontSize: 14, marginBottom: 12 }}>Últimos Jobs de Archive/Cleanup</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    <th style={{ textAlign: 'left', padding: 6, color: T.textSecondary }}>Tabela</th>
                    <th style={{ textAlign: 'center', padding: 6, color: T.textSecondary }}>Ação</th>
                    <th style={{ textAlign: 'center', padding: 6, color: T.textSecondary }}>Rows</th>
                    <th style={{ textAlign: 'center', padding: 6, color: T.textSecondary }}>Status</th>
                    <th style={{ textAlign: 'right', padding: 6, color: T.textSecondary }}>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {archiveJobs.map((j: any) => (
                    <tr key={j.id} style={{ borderBottom: `1px solid ${T.border}11` }}>
                      <td style={{ padding: 6, color: T.text }}>{j.table_name}</td>
                      <td style={{ padding: 6, textAlign: 'center', color: T.textSecondary }}>{j.action === 'ARCHIVE_R2' ? '📦' : '🗑️'} {j.action}</td>
                      <td style={{ padding: 6, textAlign: 'center', color: T.text }}>{j.rows_affected}</td>
                      <td style={{ padding: 6, textAlign: 'center', color: j.status === 'done' ? '#22c55e' : j.status === 'error' ? '#ef4444' : T.textSecondary }}>{j.status}</td>
                      <td style={{ padding: 6, textAlign: 'right', color: T.textSecondary, fontSize: 12 }}>{j.created_at?.slice(0, 16)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab Agregação */}
      {tab === 'aggregation' && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ color: T.text, fontSize: 16, margin: 0 }}>Agregação de Dados</h3>
            <button style={btn} onClick={save} disabled={saving}>{saving ? 'Salvando...' : '💾 Salvar'}</button>
          </div>

          <label style={{ color: T.textSecondary, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <input type="checkbox" checked={settings['aggregation_enabled'] !== 'false'}
              onChange={(e: any) => updateSetting('aggregation_enabled', e.target.checked ? 'true' : 'false')} />
            Agregação habilitada
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={label}>Intervalo</label>
              <select style={input} value={settings['optimizer_aggregation_interval'] || 'hourly'}
                onChange={(e: any) => updateSetting('optimizer_aggregation_interval', e.target.value)}>
                <option value="hourly">Horário</option>
                <option value="daily">Diário</option>
              </select>
            </div>
            <div>
              <label style={label}>Retenção dos agregados (dias)</label>
              <input type="number" style={input} value={settings['optimizer_aggregation_retention_days'] || '365'}
                onChange={(e: any) => updateSetting('optimizer_aggregation_retention_days', e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: 16, padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
            <div style={{ color: T.textSecondary, fontSize: 12, marginBottom: 8 }}>Tabelas Agregadas:</div>
            <div style={{ color: T.text, fontSize: 13 }}>
              📊 router_logs → router_stats_hourly<br/>
              📊 llm_usage_logs → llm_stats_hourly<br/>
              📊 wallet_transactions → wallet_daily_summary
            </div>
          </div>
        </div>
      )}

      {/* Tab Cache */}
      {tab === 'cache' && (
        <div>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: T.text, fontSize: 16, margin: 0 }}>Cache KV</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btn} onClick={save} disabled={saving}>{saving ? 'Salvando...' : '💾 Salvar'}</button>
                <button style={btnDanger} onClick={flushCache}>🧹 Flush</button>
              </div>
            </div>

            <label style={{ color: T.textSecondary, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <input type="checkbox" checked={settings['cache_enabled'] !== 'false'}
                onChange={(e: any) => updateSetting('cache_enabled', e.target.checked ? 'true' : 'false')} />
              Cache habilitado
            </label>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  <th style={{ textAlign: 'left', padding: 8, color: T.textSecondary, fontSize: 12 }}>Endpoint</th>
                  <th style={{ textAlign: 'center', padding: 8, color: T.textSecondary, fontSize: 12 }}>TTL (s)</th>
                  <th style={{ textAlign: 'center', padding: 8, color: T.textSecondary, fontSize: 12 }}>Hits 24h</th>
                  <th style={{ textAlign: 'center', padding: 8, color: T.textSecondary, fontSize: 12 }}>Misses 24h</th>
                </tr>
              </thead>
              <tbody>
                {CACHE_ENDPOINTS.map(ep => {
                  const settingKey = `optimizer_cache_${ep.key}_ttl`;
                  const stat = cacheStats.find((s: any) => s.endpoint?.includes(ep.key.replace(/_/g, '/')));
                  return (
                    <tr key={ep.key} style={{ borderBottom: `1px solid ${T.border}22` }}>
                      <td style={{ padding: 8, color: T.text, fontSize: 14 }}>{ep.label}</td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <input type="number" style={{ ...input, width: 80, textAlign: 'center' }}
                          value={settings[settingKey] || String(ep.defaultTtl)}
                          onChange={(e: any) => updateSetting(settingKey, e.target.value)} />
                      </td>
                      <td style={{ padding: 8, textAlign: 'center', color: '#22c55e', fontSize: 14 }}>{stat?.hits || 0}</td>
                      <td style={{ padding: 8, textAlign: 'center', color: T.textSecondary, fontSize: 14 }}>{stat?.misses || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab Sharding — Customer D1 Isolation */}
      {tab === 'sharding' && <ShardingTab />}
    </div>
  );
}
